/**
 * DAC profiles -- group 10.
 *
 * Eight European and Japanese line-level converters chosen so that no two share
 * a conversion mechanism *and* an output topology. The group spans a chip-based
 * delta-sigma with an op-amp buffer, a multibit delta-sigma into a FET stage, a
 * discrete Class A delta-sigma, an oversampling discrete ladder, and four
 * non-oversampling multibit designs whose analogue stages are respectively a
 * valve gain stage, a silver output transformer and a valve stage on a 16-bit
 * converter.
 *
 * Almost every manufacturer represented here publishes topology but not a full
 * electrical specification -- several publish no THD figure at all. Where that
 * is the case the entry is tagged ESTIMATED and the provenance note says which
 * numbers were quoted and which were inferred from the topology. Nothing here
 * is a measurement.
 */

import type { DacProfile } from '../../types/hardware';
import {
  FILTERS,
  HARMONIC_SHAPES,
  TRANSFORMER_LINE,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_10: DacProfile[] = [
  makeDac({
    mfr: 'Hegel Music Systems',
    model: 'HD30',
    price: usd(4800, 'APPROXIMATE', 2016, 'US retail recalled as approximately USD 4,800 at launch; figure not verified against a current dealer list.'),
    year: 2015,
    country: 'Norway',
    architecture: 'delta-sigma',
    core: 'AKM AK4490EQ delta-sigma converter, dual-mono, with synchronous reclocking ahead of the modulator',
    outputStage: 'op-amp',
    dynamicRangeDb: 118,
    thdPlusNDb: -106,
    crosstalkDb: -112,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 47,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'dsd64',
    filters: [FILTERS.sharpLinear(128)],
    effectiveBits: 19,
    jitterPsRms: 25,
    noiseShaperOrder: 5,
    confidence: 'ESTIMATED',
    accent: '#6f8fbf',
    tags: ['akm', 'reclocking', 'fixed-filter', 'nordic'],
    notes:
      'Delta-sigma core running a single fixed sharp linear-phase reconstruction filter into a low-impedance op-amp buffer. Loop feedback around the I/V and buffer stages holds the harmonic residue close to the noise floor and keeps it essentially level-independent, so the distortion signature does not change shape as level falls. Relative to the ladder designs later in this group the distinguishing traits are a flat, level-invariant distortion floor, a very low source impedance, and no zero-order-hold droop at the band edge.',
    provenanceNote:
      'Converter family, output levels and rate support are from Hegel published material; the THD+N figure corresponds to the published "less than 0.0005 percent" claim. Dynamic range, output impedance, jitter and noise-shaper order are inferred from the AK4490 datasheet class and are not Hegel figures. Filter behaviour is modelled.',
  }),

  makeDac({
    mfr: 'Electrocompaniet',
    model: 'ECD 2',
    price: usd(2650, 'APPROXIMATE', 2018, 'Recalled as roughly USD 2,650; Electrocompaniet pricing varies by market and the exact US figure is not confirmed.'),
    year: 2016,
    country: 'Norway',
    architecture: 'multibit-delta-sigma',
    core: 'Segmented multibit delta-sigma converter feeding a fully balanced FET gain stage; Electrocompaniet does not publish the converter part number',
    outputStage: 'jfet',
    dynamicRangeDb: 114,
    thdPlusNDb: -100,
    crosstalkDb: -98,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 220,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'dsd128',
    filters: [FILTERS.sharpLinear(64), FILTERS.slowLinear(48)],
    effectiveBits: 18,
    jitterPsRms: 40,
    noiseShaperOrder: 4,
    slewRateVus: 20,
    confidence: 'ESTIMATED',
    accent: '#5d7f74',
    tags: ['fet-stage', 'balanced', 'floating-ground', 'nordic'],
    notes:
      'Multibit segmented modulator into a balanced FET gain stage rather than an op-amp I/V, which is the house circuit topology and the reason this entry does not behave like the Hegel above it. The FET stage runs with only local feedback, so the residual distortion is second- and third-order and grows with output level instead of sitting at a fixed floor. Source impedance is roughly four times higher than the op-amp designs here, so the input impedance of whatever follows becomes a small but real variable in the high frequencies.',
    provenanceNote:
      'Balanced FET topology and output configuration are from Electrocompaniet published descriptions. The converter part number is not published, so the multibit delta-sigma classification is an inference from the design description. THD+N, dynamic range, output impedance and slew rate are estimates typical of this class and are not manufacturer figures.',
  }),

  makeDac({
    mfr: 'Aavik',
    model: 'D-580',
    price: usd(34000, 'APPROXIMATE', 2022, 'Flagship 580-series pricing recalled as approximately USD 34,000; not verified against a current dealer list.'),
    year: 2021,
    country: 'Denmark',
    architecture: 'delta-sigma',
    core: 'Delta-sigma conversion board with discrete Class A analogue stage and extensive on-board resonance and interference control networks',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 124,
    thdPlusNDb: -110,
    crosstalkDb: -125,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 30,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.apodizing(128), FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    effectiveBits: 20,
    jitterPsRms: 15,
    noiseShaperOrder: 6,
    idleToneLevel: 0.05,
    slewRateVus: 45,
    confidence: 'ESTIMATED',
    accent: '#8e7bb5',
    tags: ['discrete-output', 'no-op-amp', 'apodizing-default', 'low-noise-floor'],
    notes:
      'Delta-sigma conversion into a fully discrete Class A output with no op-amp in the signal path, and a design budget spent mostly on lowering the broadband noise and interference floor rather than on the last decibel of THD. The profile therefore pairs a very low noise level with a low-order, second-harmonic-weighted residue that rises with level, which is the opposite trade from the Hegel entry. The default filter is apodizing, so band-edge transients show post-ringing without the symmetrical pre-ringing of a long linear-phase FIR.',
    provenanceNote:
      'Discrete Class A output topology and the emphasis on noise and resonance control are from Aavik published material. The converter part is not published, so the delta-sigma classification is inferred. Dynamic range, THD+N, crosstalk, jitter and the filter set are estimates typical of a flagship discrete-output converter; Aavik does not publish a full electrical specification.',
  }),

  makeDac({
    mfr: 'Audiobyte',
    model: 'Hydra Vox',
    price: usd(3500, 'APPROXIMATE', 2020, 'Recalled as roughly USD 3,500 for the Vox unit alone; the matching Zap power supply is a separate purchase and is not included in this figure.'),
    year: 2019,
    country: 'Romania',
    architecture: 'discrete-r2r',
    core: 'Discrete 24-bit voltage-mode R-2R ladder with an FPGA performing interpolation and volume control',
    outputStage: 'op-amp',
    dynamicRangeDb: 118,
    thdPlusNDb: -102,
    crosstalkDb: -105,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.sharpLinear(128), FILTERS.nosCompensated(), FILTERS.apodizing(128)],
    defaultFilterId: 'sharp-linear',
    ladderMismatchLsb: 0.35,
    effectiveBits: 20,
    jitterPsRms: 20,
    internalOversampling: 8,
    confidence: 'ESTIMATED',
    accent: '#c07a4a',
    tags: ['discrete-ladder', 'fpga-interpolation', 'selectable-nos', 'desktop'],
    notes:
      'Discrete voltage-mode resistor ladder with the interpolation done in an FPGA, so changing the reconstruction filter is a genuine DSP change rather than the selection of a chipset preset. Element mismatch in the ladder is carried as a small level-dependent INL term, which is why a ladder does not clean up at low level the way a delta-sigma core does. Because the default mode oversamples, the sinc droop stays out of the audio band -- that is the specific thing separating this entry from the three non-oversampling ladders below it.',
    provenanceNote:
      'Discrete R-2R ladder architecture, FPGA digital section and DSD support are from Audiobyte published material. Output buffer topology, ladder mismatch, jitter, dynamic range and THD+N are inferred from the architecture and from the behaviour of comparable discrete ladders; Audiobyte does not publish a complete measured specification.',
  }),

  makeDac({
    mfr: 'Aries Cerat',
    model: 'Kassandra II',
    price: usd(40000, 'APPROXIMATE', 2020, 'Recalled as approximately USD 40,000; Aries Cerat sells through dealers and does not publish a fixed list price, so treat this as an order-of-magnitude figure.'),
    year: 2016,
    country: 'Cyprus',
    architecture: 'r2r',
    core: 'Multiple sign-magnitude 24-bit R-2R ladder converters paralleled per channel, run non-oversampled, into a valve gain stage',
    outputStage: 'tube',
    dynamicRangeDb: 112,
    thdPlusNDb: -70,
    crosstalkDb: -78,
    outputVrmsSE: 3.5,
    outputVrmsBal: 7.0,
    outputImpedanceOhms: 150,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    dsd: 'dsd128',
    filters: [FILTERS.nos()],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.45,
    parallelUnits: 8,
    effectiveBits: 19,
    jitterPsRms: 50,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(6, -0.9),
    slewRateVus: 15,
    confidence: 'ESTIMATED',
    accent: '#b8452f',
    tags: ['nos', 'paralleled-ladders', 'valve-output', 'choke-loaded'],
    notes:
      'Several sign-magnitude ladders are paralleled per channel, which averages element mismatch and raises available output current, then a valve gain stage with no global feedback does the voltage amplification. Running non-oversampled means there is no digital filter in the chain at all: image rejection is left to the zero-order-hold sinc envelope and the analogue bandwidth, so the profile applies the full sin(x)/x droop as a rate-dependent response. Two independent distortion mechanisms therefore stack here -- ladder INL, which is level-dependent but does not grow smoothly, and the valve transfer curve, which is strongly second-harmonic biased and rises about a decibel per decibel of drive.',
    provenanceNote:
      'Paralleled sign-magnitude ladder architecture, non-oversampling operation and the valve output stage are from Aries Cerat published descriptions. Aries Cerat does not publish THD or dynamic range, so both figures here are estimates typical of a zero-feedback valve stage on a NOS ladder, as are output impedance, jitter and ladder mismatch. The sinc droop is computed, not asserted.',
  }),

  makeDac({
    mfr: 'Kondo',
    model: 'KSL-DAC',
    price: usd(12000, 'DISCONTINUED_USED', 2024, 'Long out of production. The amount is an approximate secondhand transaction level rather than a list price, and secondhand figures for Kondo equipment vary widely with condition and provenance.'),
    year: 1998,
    country: 'Japan',
    architecture: 'ladder-multibit',
    core: 'Multibit ladder converter run without oversampling, silver-wound output transformers; Kondo does not publish the converter part number',
    outputStage: 'transformer',
    dynamicRangeDb: 104,
    thdPlusNDb: -66,
    crosstalkDb: -70,
    outputVrmsSE: 2.0,
    outputImpedanceOhms: 600,
    balanced: false,
    sampleRates: [32000, 44100, 48000],
    maxBitDepth: 20,
    dsd: 'none',
    filters: [FILTERS.nos()],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.9,
    effectiveBits: 16,
    jitterPsRms: 140,
    response: TRANSFORMER_LINE(16, 32000),
    confidence: 'ESTIMATED',
    accent: '#a8935c',
    tags: ['nos', 'silver-transformer', 'vintage', 'single-ended-only'],
    notes:
      'Non-oversampling multibit ladder with a silver-wound output transformer in place of an active line driver, so the transformer, not a feedback loop, sets the response at both ends. Finite primary inductance gives a first-order low-frequency corner and leakage inductance plus winding capacitance gives a first-order high-frequency corner, while core hysteresis lifts the low-order harmonics at low frequencies and high level. Source impedance is several hundred ohms, high enough that cable capacitance and the following input impedance shift the treble by a measurable amount rather than a theoretical one.',
    provenanceNote:
      'Transformer-coupled output, silver wiring and non-oversampling operation are from Kondo published material and period reviews. Kondo publishes no THD, dynamic range or bandwidth figures, so every electrical number in this entry is an estimate derived from the topology and the era of the converter. The transformer response corners are computed from the assumed -3 dB points and tagged as derived.',
  }),

  makeDac({
    mfr: 'Zanden Audio Systems',
    model: 'Model 5000 Signature',
    price: usd(18500, 'APPROXIMATE', 2010, 'Recalled as approximately USD 18,500 for the Signature version when current; the model has since been superseded and the figure is not verified.'),
    year: 2003,
    country: 'Japan',
    architecture: 'ladder-multibit',
    core: 'Philips TDA1541A 16-bit multibit current-divider converter with dynamic element matching, run with no digital filter',
    outputStage: 'tube',
    dynamicRangeDb: 100,
    thdPlusNDb: -72,
    crosstalkDb: -74,
    outputVrmsSE: 2.2,
    outputImpedanceOhms: 300,
    balanced: false,
    sampleRates: [44100, 48000],
    maxBitDepth: 16,
    dsd: 'none',
    filters: [FILTERS.nos()],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.7,
    effectiveBits: 16,
    jitterPsRms: 80,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(12, -1.4),
    slewRateVus: 10,
    confidence: 'ESTIMATED',
    accent: '#7d5a86',
    tags: ['nos', 'tda1541a', 'no-digital-filter', 'valve-output'],
    notes:
      'A single 16-bit current-divider converter per channel, no oversampling and no digital filter, feeding a valve output stage. Three consequences follow and all three are modelled: the full zero-order-hold droop of a 44.1 kHz sample stream, a quantisation floor set by sixteen real bits rather than twenty-four, and comparatively large ladder INL because nothing is paralleled to average element error. The valve stage supplies a second-harmonic-dominant term and a deliberate high-frequency softening that is also the only image filtering anywhere in the chain, which is why its response corner is set noticeably lower than the Aries Cerat entry above.',
    provenanceNote:
      'Converter part, absence of a digital filter and the valve output stage are from Zanden published material and period reviews. Zanden does not publish THD or dynamic range; both are estimated here from the 16-bit converter and a zero-feedback valve stage. Output impedance, jitter and ladder mismatch are inferred. The sinc droop is computed from the zero-order-hold envelope.',
  }),

  makeDac({
    mfr: 'Light Harmonic',
    model: 'Da Vinci',
    price: usd(20000, 'APPROXIMATE', 2013, 'Launch pricing recalled as around USD 20,000; configuration options moved the figure and it is not verified.'),
    year: 2012,
    country: 'United States',
    architecture: 'hybrid',
    core: 'Proprietary dual-mono converter boards with a very low phase-noise master clock; Light Harmonic does not publish the converter topology',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 120,
    thdPlusNDb: -104,
    crosstalkDb: -118,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 25,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    maxBitDepth: 32,
    dsd: 'dsd128',
    filters: [FILTERS.sharpLinear(256), FILTERS.slowLinear(48)],
    effectiveBits: 20,
    jitterPsRms: 12,
    internalOversampling: 8,
    slewRateVus: 55,
    confidence: 'ESTIMATED',
    accent: '#3f8fa8',
    tags: ['dual-mono', 'low-jitter-clock', 'long-fir', 'discrete-output'],
    notes:
      'Dual-mono converter boards with a discrete Class A output and a clocking scheme built around a very low phase-noise master oscillator, which is the parameter this design actually optimises. The default reconstruction filter is a long linear-phase FIR, so band-edge transients carry symmetrical pre- and post-ringing rather than the one-sided response of the apodizing and NOS entries in this group. Because the converter topology is not published, the conversion block here is tagged hybrid and its internal parameters are inferred; the audible fingerprint the model carries is the filter length, the low jitter figure and a low-impedance discrete buffer.',
    provenanceNote:
      'Dual-mono construction, clocking emphasis, rate support and the discrete output stage are from Light Harmonic published material. The converter architecture is not published, hence the hybrid tag and the inferred effective-bit count. THD+N, dynamic range, crosstalk, jitter and output impedance are estimates for this class, not manufacturer figures.',
  }),
];
